import { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { ApiGet } from '../Api';
import Modal from 'react-modal';
import ServerType from './ServerType';
import ServerStatus from './ServerStatus';
import Middleware from './Middleware';
import Web from './Web';
import DbBackup from './DbBackup';
import logo from './yeonoo_logo.png';
import { GetTime } from '../Function';

function YeonooMonitoring({ rotation = false }) {
  Modal.setAppElement('#root')
  
  const apiUrl = process.env.REACT_APP_API_DOMAIN + 'yeonoo/';
  const logoHeight = '60px'; //logo height + logo paddingTop
  const gridGap = window.innerWidth > 800 ? 30 : 20;
  const backgroundMainColor = 'rgb(12, 35, 64)';
  const backgroundSubColor = 'rgb(18, 46, 78)';
  const titleColor= 'rgb(153, 255, 153)';
  const channelTalkHeader = {
    'accept': 'application/json',
    'x-access-key': process.env.REACT_APP_CHANNEL_TALK_KEY,
    'x-access-secret': process.env.REACT_APP_CHANNEL_TALK_SECRET,
  };

  const [isMobile, setIsMobile] = useState(false);
  const [refreshTime, setRefreshTime] = useState('0001-01-01 00:00:00');
  const [gridStyle, setGridStyle] = useState(GetGridStyle());
  const [serverType, setServerType] = useState(GetOption());
  const [serverStatus, setServerStatus] = useState(GetOption());
  const [helpDesk, setHelpDesk] = useState(GetOption());
  const [dbBackup, setDbBackup] = useState(GetOption());
  const [channelTalkUnread, setChannelTalkUnread] = useState(GetOption());
  const [channelTalkStatus, setChannelTalkStatus] = useState(GetOption());
  const [middlewareStatus, setMiddlewareStatus] = useState(GetOption());
  const [webStatus, setWebStatus] = useState(GetOption());
  const serverTypeRef = useRef(null);
  const serverStatusRef = useRef(null);
  const helpDeskRef = useRef(null);
  const dbBackupRef = useRef(null);
  const channelTalkUnreadRef = useRef(null);
  const channelTalkStatusRef = useRef(null);
  const middlewareStatusRef = useRef(null);
  const webStatusRef = useRef(null);

  let intervalId;

  function Resize() {
    setGridStyle(GetGridStyle());
  };

  const color = { green: '#3ba272', red: '#ee6666' }

  const [ispopupDragging, setIsPopupDragging] = useState(false);

  const chartRefs = useRef([]);

  useEffect(() => {
    document.title = 'YEONOO MONITORING';

    setIsMobile(/mobile/i.test(navigator.userAgent));
    window.addEventListener('resize', Resize);

    Inquiry();

    if (rotation) {
      intervalId = setInterval(()=>{
        Inquiry();
      }, 1000 * 60);
    }

    window.addEventListener("mousemove", PopupMouseMove);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      for (const chartRef of chartRefs.current) {
        if (chartRef) {
          chartRef.dispose();
        }
      }
      chartRefs.current = [];

      setIsMobile(null);
      setRefreshTime(null);
      setGridStyle(null);
      setServerType(null);
      setServerStatus(null);
      setHelpDesk(null);
      setDbBackup(null);
      setChannelTalkUnread(null);
      setChannelTalkStatus(null);
      setMiddlewareStatus(null);
      setWebStatus(null);
      setIsPopupDragging(null);
      setIsPopupOpen(null);
      setPopupContent(null);
      setPopupName(null);
      setPopupStyle(null);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", PopupMouseMove);

    return () => {
      window.removeEventListener('mousemove', PopupMouseMove);
    };
  }, [ispopupDragging]);
  
  function ChangeFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) { //Firefox
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) { //Chrome, Safari, Opera
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { //IE/Edge
      elem.msRequestFullscreen();
    }
  }
  
  async function Inquiry() {
    DrawServerType();
    DrawServerStatus();
    DrawChannelTalkStatus();
    DrawChannelTalkUnread();
    DrawDbBackup();
    DrawHelpDesk();
    DrawMiddlewareStatus();
    DrawWebStatus();

    setRefreshTime(GetTime());
  }

  async function DrawWebStatus() {
    const response = await ApiGet(apiUrl + 'monitoring/web/status');
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [
        { name: 'ON', itemStyle: { color: color.green }},
        { name: 'OFF', itemStyle: { color: color.red }}
      ];

      for (const key in json) {
        if (json[key].status === 'ON') {
          data[0].value = json[key].value;
        } else if (json[key].status === 'OFF') {
          data[1].value = json[key].value;
        }
      }

      setWebStatus(GetOption('WEB', 'ON / OFF', data, json.length === 1 ? 'center' : undefined));
    }
  }

  async function DrawMiddlewareStatus() {
    const response = await ApiGet(apiUrl + 'monitoring/middleware/status');
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [
        { name: 'ON', itemStyle: { color: color.green }},
        { name: 'OFF', itemStyle: { color: color.red }}
      ];

      for (const key in json) {
        if (json[key].name === 'ON') {
          data[0].value = json[key].value;

        } else if (json[key].name === 'OFF') {
          data[1].value = json[key].value;
        }
      }
      
      setMiddlewareStatus(GetOption('MIDDLEWARE', 'ON / OFF', data, json.length === 1 ? 'center' : undefined));
    }
  }

  async function DrawHelpDesk() {
    const response = await ApiGet(apiUrl + 'monitoring/help-desk');
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [];

      for (const key in json[0]) {
        if (json[0][key] === 0) {
          data.push({ name: key });
        } else {
          data.push({ value: json[0][key], name: key });
        }
      }
      
      setHelpDesk(GetOption('HELP DESK', 'Type', data));
    }
  }

  async function DrawDbBackup() {
    const response = await ApiGet(apiUrl + 'monitoring/database-backup/status');
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [
        { name: 'SUCCESS', itemStyle: { color: color.green }},
        { name: 'FAIL', itemStyle: { color: color.red }}
      ];

      for (const key in json) {
        if (json[key].name === 'SUCCESS') {
          data[0].value = json[key].value;

        } else if (json[key].name === 'FAIL') {
          data[1].value = json[key].value;
        }
      }
      
      setDbBackup(GetOption('DB BACKUP', 'SUCCESS / FAIL', data, json.length === 1 ? 'center' : undefined));
    }
  }

  async function DrawChannelTalkUnread() {
    const response = await ApiGet(apiUrl + 'monitoring/channel-talk/unread', channelTalkHeader);
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [];

      let isZero = false;
      for (const key in json) {
        if (json[key] === 0) {
          isZero = true;
          data.push({ name: key });
        } else {
          data.push({ value: json[key], name: key });
        }
      }

      if (isZero) {
        setChannelTalkUnread(GetOption('CHAT UNREAD', 'Type', data, 'center'));
      } else {
        setChannelTalkUnread(GetOption('CHAT UNREAD', 'Type', data));
      }    
    }
  }

  async function DrawChannelTalkStatus() {
    const response = await ApiGet(apiUrl + 'monitoring/channel-talk/status', channelTalkHeader);
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [];

      for (const key in json) {
        if (json[key] === 0) {
          data.push({ name: key });
        } else {
          data.push({ value: json[key], name: key });
        }
      }

      setChannelTalkStatus(GetOption('CHAT STATUS', 'Type', data));   
    }
  }

  async function DrawServerStatus() {
    const response = await ApiGet(apiUrl + 'monitoring/server/status');
    if (response !== null && response.ok) {
      const json = await response.json();
      const data = [
        { name: 'ON', itemStyle: { color: color.green }},
        { name: 'OFF', itemStyle: { color: color.red }}
      ];

      for (const key in json) {
        if (json[key].name === 'ON') {
          data[0].value = json[key].value;

        } else if (json[key].name === 'OFF') {
          data[1].value = json[key].value;
        }
      }

      setServerStatus(GetOption('SERVER STATUS', 'ON / OFF', data, json.length === 1 ? 'center' : undefined));
    }
  }

  async function DrawServerType() {
    const response = await ApiGet(apiUrl + 'monitoring/server/type');
    if (response !== null && response.ok) {
      const json = await response.json();
      setServerType(GetOption('SERVER TYPE', 'Type', json));
    }
  }

  function GetGridStyle() {
    return {
      display: 'grid',
      gap: gridGap,
      padding: gridGap,
      backgroundColor: backgroundMainColor,
      width: '100vw',
      height: `calc(100vh - ${logoHeight})`,
      boxSizing: 'border-box', // 패딩을 포함하여 크기 계산
      overflow: 'auto',
      scrollbarWidth: 'none',
      gridTemplateColumns: window.innerWidth > 800 ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)'
    };
  };

  const gridItemStyle = {
    backgroundColor: backgroundSubColor,
    borderRadius: '10px',
    height: `calc((100vh - ${gridGap*3}px - ${logoHeight}) / 2)`, // 행 수에 맞게 높이를 계산, 2행이니 세로 간격의 총합 = padding 20px * 2 + gap 20px = 60px,
  };

  function GetOption(title, dataName, data, position = 'inside') { 
    return {
      title: {
        text: title,
        left: 'center',
        top: window.innerWidth > 800 ? 25 : 15,
        textStyle: {
          color: titleColor,
          fontSize: window.innerWidth > 800 ? 30 : 20
        },
      },
      tooltip: {
        trigger: 'item',
        //formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'horizontal',
        bottom: window.innerWidth > 800 ? 25 : 15,
        left: 'center',
        textStyle: {
          color: 'white',
          fontSize: window.innerWidth > 800 ? 20 : 12
        },
        itemWidth: window.innerWidth > 800 ? 20 : 12, //color box width
        itemHeight: window.innerWidth > 800 ? 20 : 12 //color box height
      },
      //color: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'], //색 배열 설정
      series: [
        {
          name: dataName,
          type: 'pie',
          radius: window.innerWidth > 800 ? '60%' : '80%', //['40%', '70%']
          data: data,
          label: {
            color: 'white',
            fontSize: window.innerWidth > 800 ? 30 : 20,
            textBorderColor: 'black',  // 텍스트 테두리 색상 설정
            textBorderWidth: 1,  // 텍스트 테두리 두께 설정
            show: true,  //항상 레이블을 표시
            position: position, //outside로 하면 바깥쪽
            formatter: '{c}' //{b} : {c} b는 name, c는 value
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  }

  function HideToolTip(instance) {
    //팝업 열기 전에 Tool Tip 제거(터치로 팝업 열 때 제거되지 않아 팝업 위로 보이는 문제 방지)
    instance.dispatchAction({
      type: 'hideTip'
    });
  }

  async function OnClickChart(params, instance) {
    setPopupStyle((previousState) => {
      const previousStyle = {...previousState};

      previousStyle.content.width = 0;  
      previousStyle.content.height = 0;  

      return previousStyle;
    });

    HideToolTip(serverStatusRef.current.getEchartsInstance());
    HideToolTip(middlewareStatusRef.current.getEchartsInstance());
    HideToolTip(webStatusRef.current.getEchartsInstance());
    HideToolTip(serverTypeRef.current.getEchartsInstance());
    HideToolTip(dbBackupRef.current.getEchartsInstance());
    HideToolTip(helpDeskRef.current.getEchartsInstance());
    HideToolTip(channelTalkStatusRef.current.getEchartsInstance());
    HideToolTip(channelTalkUnreadRef.current.getEchartsInstance());

    const chartName = instance.getOption().title[0].text;
    if (chartName === 'SERVER STATUS') {
      HideToolTip(instance);
      setPopupContent(<ServerStatus serverStatus={params.name} setPopupStyle={setPopupStyle}/>);
      setPopupName(chartName);
      setIsPopupOpen(true);
    }else if (chartName === 'MIDDLEWARE') {
      HideToolTip(instance);
      setPopupContent(<Middleware middlewareStatus={params.name} setPopupStyle={setPopupStyle}/>);
      setPopupName(chartName);
      setIsPopupOpen(true);
    } else if (chartName === 'WEB') {
      HideToolTip(instance);
      setPopupContent(<Web webStatus={params.name} setPopupStyle={setPopupStyle}/>);
      setPopupName(chartName);
      setIsPopupOpen(true);
    } else if (chartName === 'SERVER TYPE') {
      HideToolTip(instance);
      setPopupContent(<ServerType serverType={params.name} setPopupStyle={setPopupStyle}/>);
      setPopupName(chartName);
      setIsPopupOpen(true);
    } else if (chartName === 'DB BACKUP') {
      HideToolTip(instance);
      setPopupContent(<DbBackup dbBackupStatus={params.name} setPopupStyle={setPopupStyle}/>);
      setPopupName(chartName);
      setIsPopupOpen(true);
    }
  }

  function OnChartReady(instance) {
    instance.on('click', (params) => OnClickChart(params, instance));

    chartRefs.current.push(instance);

    return () => {
      instance.off('click', OnClickChart);
    };
  }
  function PopupMouseMove(event) {
    if (ispopupDragging && event.buttons === 1) {
      setPopupStyle((previousState) => {
        const previousStyle = {...previousState};

        //팝업창이 화면 밖으로 나가지 않을 때까지만 이동
        if (previousStyle.content.top + event.movementY - popupStyle.content.height/2 > 0 && 
          previousStyle.content.left + event.movementX - popupStyle.content.width/2 > 0 && 
          previousStyle.content.top + event.movementY + popupStyle.content.height/2 < window.innerHeight && 
          previousStyle.content.left + event.movementX + popupStyle.content.width/2 < window.innerWidth) {
          
            previousStyle.content.left = previousStyle.content.left + event.movementX;
            previousStyle.content.top = previousStyle.content.top + event.movementY;
        }

        return previousStyle;
      });
     }
  }
  //popup drag
  function PopupTopBarMouseDown(event) {
    //setClickPosition({ x:event.clientX, y:event.clientY });
    //setCursorPosition({ clickX:event.nativeEvent.offsetX, clickY:event.nativeEvent.offsetY });
    setIsPopupDragging(true);
  }
  function PopupMouseUp() {
    setIsPopupDragging(false);
  }

  //팝업창
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState(<div></div>);
  const [popupName, setPopupName] = useState('');
  const [popupStyle, setPopupStyle] = useState({
    overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    content: {
    width: '350px',
    height: '500px',
    borderRadius: '5px',
    padding: '0',
    backgroundColor: '#F8F8F8',
    transform: 'translate(-50%, -50%)',
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
    overflow: 'auto'
    }
  });
  const popupModal = (
    <div onMouseUp={PopupMouseUp}>
      <Modal isOpen={isPopupOpen} style={popupStyle} onAfterOpen={OnAfterOpenPopup} /*onRequestClose={OnRequestClosePopup} /*modal 밖 클릭했을 때 나가짐*/>
        <div onMouseDown={PopupTopBarMouseDown} style={{ width: '100%', backgroundColor: backgroundSubColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'move', userSelect: 'none' }}>
          <span style={{ color: 'white', margin: '10px' }}>{popupName}</span>
          <button style={{ width: '25px', margin: '10px', padding: '0px' }} onClick={OnRequestClosePopup}>✕</button>
        </div>

        {popupContent}
      </Modal>
    </div>
   );
   function OnAfterOpenPopup() {
    setPopupStyle((previousState) => {
      const previousStyle = {...previousState};
 
      previousStyle.content.top = window.innerHeight / 2;
      previousStyle.content.left = window.innerWidth / 2;
      previousStyle.content.transform = 'translate(-50%, -50%)';
 
      return previousStyle;
    });
   }
   function OnRequestClosePopup() {
    setIsPopupOpen(false);
    setPopupContent(<div></div>);
   }

  function Reload() {
    //재조회되는 이펙트를 보여주기 위해 조회 전 데이터 초기화
    const option =  GetOption();
    setServerType(option);
    setServerStatus(option);
    setHelpDesk(option);
    setDbBackup(option);
    //setChannelTalkUnread(option);
    //setChannelTalkStatus(option);
    setMiddlewareStatus(option);
    setWebStatus(option);

    Inquiry();
  }

  const buttonStyle = { backgroundColor: backgroundMainColor, color: 'rgb(204,204,204)', border: 'none', fontSize: window.innerWidth > 800 ? 30 : 40 };
  const dualChartStyle = { display: 'grid', backgroundColor: backgroundSubColor, gridTemplateColumns: 'repeat(2, 1fr)', borderRadius: '10px' };
  const eChartsStyle = { width: '100%', height: '100%' };

  return (
    <div onContextMenu={(event)=>{ event.preventDefault(); }}>
      <div style={{ backgroundColor: backgroundMainColor, paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
        <img src={logo} width={'200px'} height={'40px'} style={{ marginLeft: 10 }}/>

        <div style={{ marginRight: gridGap }}>
          <button title={refreshTime} onClick={Reload} style={buttonStyle}>⟳</button>
          {!isMobile && <button title={document.fullscreenElement ? '최소화' : '최대화'} onClick={ChangeFullscreen} style={buttonStyle}>◱</button>}
        </div>
      </div>

      <div style={gridStyle}>
        <div style={dualChartStyle}>
          <div style={gridItemStyle}>
            <ReactECharts ref={serverTypeRef} option={serverType} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>

          <div style={gridItemStyle}>
            <ReactECharts ref={serverStatusRef} option={serverStatus} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>
        </div>

        <div style={dualChartStyle}>
          <div style={gridItemStyle}>
            <ReactECharts ref={helpDeskRef} option={helpDesk} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>

          <div style={gridItemStyle}>
            <ReactECharts ref={dbBackupRef} option={dbBackup} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>
        </div>
 
        <div style={dualChartStyle}>
          <div style={gridItemStyle}>
            <ReactECharts ref={middlewareStatusRef} option={middlewareStatus} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>

          <div style={gridItemStyle}>
            <ReactECharts ref={webStatusRef} option={webStatus} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>
        </div>

        <div style={dualChartStyle}>
          <div style={gridItemStyle}>
            <ReactECharts ref={channelTalkUnreadRef} option={channelTalkUnread} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>

          <div style={gridItemStyle}>
            <ReactECharts ref={channelTalkStatusRef} option={channelTalkStatus} style={eChartsStyle} onChartReady={OnChartReady}/>
          </div>
        </div>
      </div>

      {popupModal}
    </div>
  );
}

export default YeonooMonitoring;